/**
 * useIntegracion Hook
 * 
 * React hook para consumir los datos de Deenex Multi desde el backend.
 * Utiliza los nuevos endpoints /api/v1/integracion/*
 */

import { useState, useCallback } from 'react';
import { apiClient } from '../services/api/client';

// ============================================================================
// Types
// ============================================================================

export interface DeenexMultiUsuario {
    _id: string;
    email: string;
    nombreCompleto?: string;
    role: string;
    idMarca?: string;
    locales?: string[];
    permisos?: Record<string, { view: boolean; edit: boolean; create: boolean }>;
    activo: boolean;
    lastLogin?: string;
}

export interface DeenexMultiMarca {
    _id: string;
    appName: string;
    appLogo?: string;
    images?: {
        logo?: string;
        favicon?: string;
    };
    domain?: string;
    colors?: Array<{ name: string; value: string }>;
    systemOptions?: {
        deliverySystem: boolean;
        tableSystem: boolean;
    };
    deliveryCondiciones?: {
        minimoCompra: number;
        deliveryRange: number;
    };
}

export interface DeenexMultiLocal {
    _id: string;
    nameLocal: string;
    addressLocal?: string;
    schedule?: Record<string, unknown>;
    paymentMethods?: Record<string, { enabled: boolean }>;
    statusLocal: boolean;
    whatsapp?: string;
}

export interface DeenexMultiMenu {
    categories: Array<{ _id: string; name: string; sortingPosition?: number }>;
    subcategories: Array<{ _id: string; name: string; category: { id: string } }>;
    products: Array<{
        _id: string;
        name: string;
        price: number;
        category: { id: string };
        subCategory?: { id: string };
        sku?: string;
        active: boolean;
    }>;
    opcionales: Array<{ _id: string; name: string; price: number; sku?: string }>;
}

export interface DeenexMultiPedido {
    _id: string;
    total: number;
    items: unknown[];
    user?: Record<string, unknown>;
    type: 'delivery' | 'takeaway' | 'mesa';
    paymentStatus: string;
    createdAt: string;
}

export interface PedidosFilters {
    localId?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    estado?: 'pending' | 'completed' | 'cancelled';
    limit?: number;
    page?: number;
}

export interface PaginatedPedidos {
    success: boolean;
    total: number;
    page: number;
    limit: number;
    data: DeenexMultiPedido[];
}

export interface UsuarioSimple {
    _id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
    lastLoginAt: string | null;
    brandId: string | null;
    createdAt: string;
    birthDate?: string;
    gender?: string;
}

export interface PaginatedUsuarios {
    success: boolean;
    total: number;
    totalDatabaseValue?: number;
    potentialDatabaseValue?: number;
    potentialGrowthPercentage?: number;
    page: number;
    limit: number;
    data: UsuarioSimple[];
}

interface UseIntegracionState {
    loading: boolean;
    error: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchUsuarios(page = 1, limit = 50): Promise<PaginatedUsuarios> {
    const response = await apiClient.get('/integracion/usuarios', { params: { page, limit } });
    return response.data;
}

async function fetchUsuarioActual(): Promise<DeenexMultiUsuario | null> {
    const response = await apiClient.get('/integracion/usuario');
    return response.data.data;
}

async function fetchMarca(): Promise<DeenexMultiMarca | null> {
    const response = await apiClient.get('/integracion/marca');
    return response.data.data;
}

async function fetchLocales(): Promise<DeenexMultiLocal[]> {
    const response = await apiClient.get('/integracion/locales');
    return response.data.data || [];
}

async function fetchMenu(localId: string): Promise<DeenexMultiMenu | null> {
    const response = await apiClient.get(`/integracion/menu/${localId}`);
    return response.data.data;
}

async function fetchPedidos(filters?: PedidosFilters): Promise<PaginatedPedidos> {
    const response = await apiClient.get('/integracion/pedidos', { params: filters });
    return response.data;
}

async function checkHealth(): Promise<{ deenexMulti: string; timestamp: string }> {
    const response = await apiClient.get('/integracion/health');
    return response.data;
}

// ============================================================================
// Hook
// ============================================================================

export function useIntegracion() {
    const [state, setState] = useState<UseIntegracionState>({
        loading: false,
        error: null,
    });

    const [usuarios, setUsuarios] = useState<PaginatedUsuarios | null>(null);
    const [usuario, setUsuario] = useState<DeenexMultiUsuario | null>(null);
    const [marca, setMarca] = useState<DeenexMultiMarca | null>(null);
    const [locales, setLocales] = useState<DeenexMultiLocal[]>([]);
    const [menu, setMenu] = useState<DeenexMultiMenu | null>(null);
    const [pedidos, setPedidos] = useState<PaginatedPedidos | null>(null);

    const setLoading = (loading: boolean) => setState(prev => ({ ...prev, loading }));
    const setError = (error: string | null) => setState(prev => ({ ...prev, error, loading: false }));

    // Fetch lista de usuarios
    const getUsuarios = useCallback(async (page = 1, limit = 50) => {
        setLoading(true);
        try {
            const data = await fetchUsuarios(page, limit);
            setUsuarios(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener usuarios';
            setError(msg);
            return null;
        }
    }, []);

    // Fetch usuario actual
    const getUsuario = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchUsuarioActual();
            setUsuario(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener usuario';
            setError(msg);
            return null;
        }
    }, []);

    // Fetch marca
    const getMarca = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchMarca();
            setMarca(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener marca';
            setError(msg);
            return null;
        }
    }, []);

    // Fetch locales
    const getLocales = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchLocales();
            setLocales(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener locales';
            setError(msg);
            return [];
        }
    }, []);

    // Fetch menu
    const getMenu = useCallback(async (localId: string) => {
        setLoading(true);
        try {
            const data = await fetchMenu(localId);
            setMenu(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener menú';
            setError(msg);
            return null;
        }
    }, []);

    // Fetch pedidos
    const getPedidos = useCallback(async (filters?: PedidosFilters) => {
        setLoading(true);
        try {
            const data = await fetchPedidos(filters);
            setPedidos(data);
            setError(null);
            return data;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al obtener pedidos';
            setError(msg);
            return null;
        }
    }, []);

    // Health check
    const healthCheck = useCallback(async () => {
        try {
            return await checkHealth();
        } catch {
            return { deenexMulti: 'error', timestamp: new Date().toISOString() };
        }
    }, []);

    // Search AI
    const searchAudience = useCallback(async (prompt: string) => {
        setLoading(true);
        try {
            const response = await apiClient.post('/integracion/audience/search-ai', { prompt });
            setLoading(false);
            return response.data; // { success: true, data: [], ai: { explanation: '' } }
        } catch (err: any) {
            setLoading(false);
            if (err.response?.status === 422) {
                // Clarification needed
                return err.response.data; // { needsClarification: true, question: '' }
            }
            const msg = err.response?.data?.message || err.message || 'Error en búsqueda IA';
            setError(msg);
            return null;
        }
    }, []);

    return {
        // State
        loading: state.loading,
        error: state.error,

        // Data
        usuarios,
        usuario,
        marca,
        locales,
        menu,
        pedidos,

        // Actions
        getUsuarios,
        getUsuario,
        getMarca,
        getLocales,
        getMenu,
        getPedidos,
        healthCheck,
        searchAudience, // New method
    };
}

export default useIntegracion;
